import { isOriginInspectionDocument } from '../document/document-types';
import { documentBankRequired, supportsDocumentField } from '../document/field-policy';
import { validateOriginInspection } from './origin-inspection-validation';
import Decimal from 'decimal.js';
import { validateInsurance } from './insurance-validation';

import type { DocumentType } from '../../server/contracts';
import { calculateAmounts } from './amounts';
import { tradeInputSchema, type TradeInput } from './schema';
import { isSettlementDocument, isTransportDocument } from '../document/document-types';
import { validateSettlement } from './settlement-validation';
import { validateTransport } from './transport-validation';

export type ValidationIssue = Readonly<{
  code: string;
  path: string;
  severity: 'ERROR' | 'WARNING';
  message: string;
}>;

export function validateTrade(input: TradeInput, type: DocumentType): ValidationIssue[] {
  const parsed = tradeInputSchema.safeParse(input);
  if (!parsed.success) {
    return parsed.error.issues.map((issue) => ({
      code: 'STRUCTURE_INVALID',
      path: issue.path.join('.'),
      severity: 'ERROR' as const,
      message: issue.message,
    }));
  }
  const trade = parsed.data;
  if (isOriginInspectionDocument(type)) return validateOriginInspection(trade, type);
  if (type === 'INSURANCE_POLICY') return validateInsurance(trade);
  if (isTransportDocument(type)) return validateTransport(trade, type);
  if (isSettlementDocument(type)) return validateSettlement(trade, type);
  const isPurchaseOrder = type === 'PURCHASE_ORDER';
  const isShippingOrder = type === 'SHIPPING_ORDER';
  const issues: ValidationIssue[] = [];
  const add = (code: string, path: string, severity: 'ERROR' | 'WARNING', message: string) => {
    issues.push({ code, path, severity, message });
  };

  if (isShippingOrder) {
    const requiredHeaderFields = [
      ['SO_VESSEL_MISSING', 'vessel', 'Vessel is missing.'],
      ['SO_VOYAGE_MISSING', 'voyageNumber', 'Voyage number is missing.'],
      ['SO_PORT_OF_LOADING_MISSING', 'portOfLoading', 'Port of loading is missing.'],
      ['SO_PORT_OF_DISCHARGE_MISSING', 'portOfDischarge', 'Port of discharge is missing.'],
      ['SO_SHIPPER_MISSING', 'shipper', 'Shipper is missing.'],
      ['SO_CONSIGNEE_MISSING', 'consignee', 'Consignee is missing.'],
      ['SO_NOTIFY_PARTY_MISSING', 'notifyParty', 'Notify party is missing.'],
      ['SO_FREIGHT_TERMS_MISSING', 'freightTerms', 'Freight terms are missing.'],
      ['SO_ETD_MISSING', 'etd', 'ETD is missing.'],
      ['SO_CUTOFF_MISSING', 'cutOff', 'Cut-off is missing.'],
      ['SO_SPECIAL_REQUIREMENTS_MISSING', 'specialRequirements', 'Special requirements are missing.'],
    ] as const;
    for (const [code, path, message] of requiredHeaderFields) {
      if (!trade[path]) add(code, path, 'WARNING', message);
    }
    if (!trade.carrierSignature) add('SO_CARRIER_SIGNATURE_MISSING', 'carrierSignature', 'WARNING', 'Carrier signature is missing.');
    if (trade.items.length === 0) add('SO_ITEM_REQUIRED', 'items', 'WARNING', 'At least one cargo row is required.');
    trade.items.forEach((item, index) => {
      const base = `items.${index}`;
      if (!item.name && !item.description) add('SO_ITEM_NAME_MISSING', `${base}.name`, 'WARNING', 'Cargo description is missing.');
      for (const [field, label] of [['marks', 'Marks'], ['packages', 'Packages'], ['grossWeight', 'Gross weight'], ['measurement', 'Measurement'], ['container', 'Container']] as const) {
        if (!item[field]) add(`SO_${field.toUpperCase()}_MISSING`, `${base}.${field}`, 'WARNING', `${label} is missing.`);
      }
    });
    return issues;
  }

  if (!isPurchaseOrder && !trade.buyer.name) add('BUYER_REQUIRED', 'buyer.name', 'ERROR', 'Buyer name is required.');
  if (!isPurchaseOrder && type !== 'QUOTATION' && !trade.buyer.address) add('BUYER_ADDRESS_MISSING', 'buyer.address', 'WARNING', 'Buyer address is missing.');
  if (!isPurchaseOrder && type !== 'QUOTATION' && !trade.buyer.country) add('BUYER_COUNTRY_MISSING', 'buyer.country', 'WARNING', 'Buyer country is missing.');
  if (type === 'QUOTATION' && !trade.buyer.attention) {
    add('BUYER_ATTENTION_MISSING', 'buyer.attention', 'WARNING', 'Buyer attention is missing.');
  }
  if (!trade.currency) add('CURRENCY_REQUIRED', 'currency', isPurchaseOrder ? 'WARNING' : 'ERROR', 'Currency is required.');
  if (trade.items.length === 0) add('ITEM_REQUIRED', 'items', isPurchaseOrder ? 'WARNING' : 'ERROR', 'At least one item is required.');

  trade.items.forEach((item, index) => {
    const base = `items.${index}`;
    if (!item.name) add('ITEM_NAME_REQUIRED', `${base}.name`, isPurchaseOrder ? 'WARNING' : 'ERROR', 'Item name is required.');
    if (!item.model) add('ITEM_MODEL_MISSING', `${base}.model`, 'WARNING', 'Item model is missing.');
    if (!item.unit) add('ITEM_UNIT_REQUIRED', `${base}.unit`, isPurchaseOrder ? 'WARNING' : 'ERROR', 'Item unit is required.');
    if (item.quantity === null || new Decimal(item.quantity).lte(0)) {
      add('QUANTITY_INVALID', `${base}.quantity`, isPurchaseOrder ? 'WARNING' : 'ERROR', 'Quantity must be greater than zero.');
    } else if (item.unit?.toUpperCase() === 'PCS' && !new Decimal(item.quantity).isInteger()) {
      add('UNIT_REQUIRES_INTEGER', `${base}.quantity`, isPurchaseOrder ? 'WARNING' : 'ERROR', 'PCS quantity must be an integer.');
    }
    if (item.unitPrice === null) {
      add('UNIT_PRICE_REQUIRED', `${base}.unitPrice`, isPurchaseOrder || type === 'SALES_CONTRACT' ? 'WARNING' : 'ERROR', 'Unit price is required; SC calculations use zero when it is omitted.');
    } else if (new Decimal(item.unitPrice).eq(0) && !item.freeSampleReason) {
      add('FREE_SAMPLE_REASON_REQUIRED', `${base}.freeSampleReason`, isPurchaseOrder ? 'WARNING' : 'ERROR', 'A zero-price sample requires a reason.');
    }
  });

  if (documentBankRequired(type, trade.paymentMethod) && !trade.bankAccountVersionId) {
    add('BANK_ACCOUNT_REQUIRED', 'bankAccountVersionId', isPurchaseOrder ? 'WARNING' : 'ERROR', 'T/T payment requires a confirmed bank account.');
  }
  if (supportsDocumentField(type, 'paymentTerms') && !trade.paymentTerms) add('PAYMENT_TERMS_MISSING', 'paymentTerms', 'WARNING', 'Payment terms are missing.');
  if (supportsDocumentField(type, 'validityDate') && !trade.validityDate) add('VALIDITY_MISSING', 'validityDate', 'WARNING', 'Validity date is missing.');
  if (isPurchaseOrder) {
    if (!trade.supplier.name) add('PO_SUPPLIER_MISSING', 'supplier.name', 'WARNING', 'Supplier is missing.');
    if (!trade.deliveryTerms) add('PO_DELIVERY_TERMS_MISSING', 'deliveryTerms', 'WARNING', 'Delivery terms are missing.');
    if (!trade.deliveryDate) add('PO_DELIVERY_DATE_MISSING', 'deliveryDate', 'WARNING', 'Delivery date is missing.');
    if (!trade.shippingMethod) add('PO_SHIPPING_METHOD_MISSING', 'shippingMethod', 'WARNING', 'Shipping method is missing.');
    if (!trade.shippingMarks) add('PO_SHIPPING_MARKS_MISSING', 'shippingMarks', 'WARNING', 'Shipping marks are missing.');
  } else if (type === 'PROFORMA_INVOICE') {
    if (!trade.incoterm) add('INCOTERM_REQUIRED', 'incoterm', 'ERROR', 'PI requires an Incoterm.');
    if (!trade.namedPlace) add('NAMED_PLACE_REQUIRED', 'namedPlace', 'ERROR', 'PI requires a named place.');
    if (!trade.deliveryDate) add('DELIVERY_MISSING', 'deliveryDate', 'WARNING', 'Delivery date is missing.');
  } else {
    if (supportsDocumentField(type, 'incoterm') && !trade.incoterm) add('INCOTERM_MISSING', 'incoterm', 'WARNING', 'Incoterm is missing.');
    if (supportsDocumentField(type, 'namedPlace') && trade.incoterm && !trade.namedPlace) add('NAMED_PLACE_MISSING', 'namedPlace', 'WARNING', 'Named place is missing.');
  }

  if (type === 'SALES_CONTRACT') {
    const clauses = [
      ['SC_SHIPPING_TERMS_MISSING', 'shippingTerms', 'Shipping terms are missing.'],
      ['SC_PAYMENT_CLAUSE_MISSING', 'paymentClause', 'Payment clause is missing.'],
      ['SC_INSURANCE_CLAUSE_MISSING', 'insuranceClause', 'Insurance clause is missing.'],
      ['SC_DOCUMENTS_REQUIRED_MISSING', 'documentsRequired', 'Required documents are missing.'],
      ['SC_INSPECTION_CLAUSE_MISSING', 'inspectionClause', 'Inspection clause is missing.'],
      ['SC_FORCE_MAJEURE_CLAUSE_MISSING', 'forceMajeureClause', 'Force majeure clause is missing.'],
      ['SC_CLAIMS_CLAUSE_MISSING', 'claimsClause', 'Claims clause is missing.'],
      ['SC_ARBITRATION_CLAUSE_MISSING', 'arbitrationClause', 'Arbitration clause is missing.'],
    ] as const;
    for (const [code, path, message] of clauses) {
      if (!trade[path]) add(code, path, 'WARNING', message);
    }
  }

  const amounts = calculateAmounts(trade, {
    missingAdjustmentsAsZero: isPurchaseOrder || type === 'SALES_CONTRACT',
    missingItemPricesAsZero: type === 'SALES_CONTRACT',
  });
  if (amounts.total === null) {
    add('AMOUNT_INCOMPLETE', 'total', isPurchaseOrder ? 'WARNING' : 'ERROR', 'Amount inputs are incomplete.');
  } else if (new Decimal(amounts.total).lte(0)) {
    add('TOTAL_MUST_BE_POSITIVE', 'total', isPurchaseOrder ? 'WARNING' : 'ERROR', 'Total must be greater than zero.');
  }
  return issues;
}
